import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../utils/axios";
import { Button, Progress, Skeleton, Space } from "antd";
import useLang from "../../hooks/useLang";
import { getUserRoleFromToken } from "../../utils/Auth";
import "./Dashboard.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Table,
	TableBody,
	TableContainer,
	TableRow,
	TableCell,
} from "@mui/material";
import { ReactComponent as ClockIcon } from "../../assets/clock-time.svg";
import { ReactComponent as CalendarIcon } from "../../assets/calendar-event-month.svg";
import { ReactComponent as ButtonIcon } from "../../assets/arrow-export-popout.svg";
import { ReactComponent as FilterIcon } from "../../assets/filter-three-lines.svg";
import { ReactComponent as SliderRight } from "../../assets/sliderRight.svg";
import { ReactComponent as SliderLeft } from "../../assets/sliderLeft.svg";
import OrderStatus from "../../components/OrderStatus";
import { initialTableData } from "../../lib/InitialOrderStatus";
import {
	AreaChart,
	Area,
	Tooltip,
	CartesianGrid,
	XAxis,
	YAxis,
	ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import { orderStatistic } from "../../redux/orderSlice";
import { financeStatistic } from "../../redux/financeSlice";
import { Pie } from "recharts";
import { orderStatuses, userRoles } from "../../types/enums";
import Slider from "react-slick";
import { AgGridReact } from "ag-grid-react";
import { ReactComponent as KosovaHart } from "../../assets/kosovoLow.svg";
import Map from "./Map";
import { showMessageModal } from "../../redux/messageModalSlice";
import ProgressListTable, {
	ProgressListItem,
} from "../../components/ProgressListTable";
import VolumeServiceLevelChart, {
	VolumeServiceData,
} from "../../components/VolumeServiceLevelChart";
import SuccessRateChart from "../../components/SuccessRateChart";
import ProcessChart from "../../components/ProgressChart";
import MySliderToggle from "../../components/MySliderToggle";
import classes from "../../components/FilterOrderSection.module.css";
import DefaultSelect from "../../components/form/DefaultSelect";
import { fetchZones } from "../../lib/OptionListApi";
import DefaultDatePicker from "../../components/form/DefaultDatePicker";
import DailySalesPieChart, {
	IPieChartData,
} from "../../components/DailySalesPieChart";
interface IInternationalWeeklyStatisticsDto {
	zoneName: string;
	deliveredDayName: string;
	deliveredDayNumber: number;
	numberOfDeliveredOrders: number;
	percentageOfZone: number;
}
interface ITop5ClientsForPaymentsDto {
	sellerId: number;
	nameAndSurname: string;
	paymentAmount: number;
	paymentPercentage: number;
}

interface IOrderDataChart {
	orderMonth: string;
	deliveredOrded: number;
	rejectedOrders: number;
}
interface Zone {
	id: number;
	description: string;
	levelId: number;
	parentId: number;
}

interface OrderCountByCity {
	city: string;
	orderCount: number;
	countryId: number;
	percentage: string;
}

const dayAbbreviations: Record<string, string> = {
	Sunday: "Sun",
	Monday: "Mon",
	Tuesday: "Tue",
	Wednesday: "Wed",
	Thursday: "Thu",
	Friday: "Fri",
	Saturday: "Sat",
};

export interface IWeeklyOrdersData {
	day: string;
	dorezuar: number;
	kthyer: number;
}

interface ISuccessRateDeliveredOrReturnedDto {
	statusName: string;
	dayName: string;
	dayNumber: number;
	numberOfOrders: number;
	percentageOfStatus: number;
}
export interface IProcessData {
	date: string;
	inTransport: number;
	inStorage: number;
	pending: number;
}
interface IOrderProcessStatisticsDto {
	theDate: string;
	inStorageCount: number;
	transportingCount: number;
	pendingCount: number;
}

interface IZoneDeliveryDto {
	zoneName: string;
	numberOfDeliveredOrders: number;
	percentageOfZone: number;
}

const colorMapping: Record<string, string> = {
	KS: "#4f16c2",
	AL: "#FFB5B5",
	MK: "#28BFFF",
};

const zoneMapping: Record<string, string> = {
	KS: "Kosova",
	AL: "Shqipëria",
	MK: "Maqedonia",
};

const initialCountryTableData = [
	{
		country: "Kosovo",
		orderCount: 0,
	},
	{
		country: "Albania",
		orderCount: 0,
	},
	{
		country: "Macedonia",
		orderCount: 0,
	},
];

const albanianMonths = [
	"Janar",
	"Shkurt",
	"Mars",
	"Prill",
	"Maj",
	"Qershor",
	"Korrik",
	"Gusht",
	"Shtator",
	"Tetor",
	"Nëntor",
	"Dhjetor",
];

const Dashboard = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [data, setData] = useState([]);
	const [tableOrderByCountries, setTableOrderByCountries] = useState(
		initialCountryTableData
	);
	const [deliveredOrdersCountByCity, setDeliveredOrdersCountByCity] =
		useState<any[]>([]);
	const [returnedOrdersCountByCity, setReturnedOrdersCountByCity] = useState<
		any[]
	>([]);
	const [
		deliveredOrdersCountByCityForMap,
		setDeliveredOrdersCountByCityForMap,
	] = useState<any[]>([]);
	const [
		returnedOrdersCountByCityForMap,
		setReturnedOrdersCountByCityForMap,
	] = useState<any[]>([]);
	const [ordersCount, setOrdersCount] = useState<any[]>(initialTableData);
	const [companyBalance, setCompanyBalance] = useState<any>([]);
	const [isClient, setIsClient] = useState<boolean>();
	const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [zones, setZones] = useState<Zone[]>([]);
	const [countryId, setCountryId] = useState(null);
	const { strings, lang } = useLang("dashboard");

	const orderCountByStatus = (data: any) => {
		const updatedData = initialTableData.map((item) => {
			const matchingItemFromAPI = data?.find(
				(apiItem: any) => apiItem.statusId === item.id
			);
			return matchingItemFromAPI
				? {
						...item,
						numberOfOrders: matchingItemFromAPI.numberOfOrders,
				  }
				: item;
		});

		const translatedData = translateOrderDescriptions(updatedData, strings);
		setOrdersCount(translatedData);
		// setOrdersCount(updatedData);
	};

	const getOrderCount = useCallback(
		async (startDate: any, endDate: any, selectedCountry: any) => {
			try {
				const formattedStartDate = startDate
					? startDate?.toISOString().split("T")[0]
					: null;
				const formattedEndDate = endDate
					? endDate?.toISOString().split("T")[0]
					: null;
				const response = await api.get(
					"Dashboard/order-counts-per-status",
					{
						params: {
							countryId: selectedCountry || null,
							fromDate: formattedStartDate,
							toDate: formattedEndDate,
						},
					}
				);
				if (response.status === 200) {
					const result = response?.data;
					setData(result);
					orderCountByStatus(result);
					dispatch(orderStatistic(result));
					return result;
				}
			} catch (error) {
				console.log(error);
			}
		},
		[dispatch, endDate, orderCountByStatus, startDate]
	);

	const [dailyOrderList, setDailyOrderList] = useState([]);

	const getDailyOrderList = async () => {
		try {
			const response = await api.get("Order/dailyOrderList");
			if (response.status === 200) {
				const result = response.data;
				setDailyOrderList(result);
			} else {
				console.error(
					"Failed to fetch daily order list. Status:",
					response.status
				);
			}
		} catch (error) {
			console.error("Error fetching daily order list:", error);
		}
	};

	const [top5ClientsForOrderPayments, setTop5lientsForOrderPayments] =
		useState<ProgressListItem[] | undefined>();

	const [top5ClientsForTransportPayments, setTop5lientsForTransportPayments] =
		useState<ProgressListItem[] | undefined>();
	const [internationalWeeklyStatistics, setInternationalWeeklyStatistics] =
		useState<VolumeServiceData[] | undefined>();
	const [weeklyData, setWeeklyData] = useState<IWeeklyOrdersData[]>([]);
	const [statisticsForOrdesInProcess, setStatisticsForOrdesInProcess] =
		useState<IProcessData[]>([]);

	const getTop5ClientsForTransportPayments = async () => {
		try {
			const response = await api.get(
				"Dashboard/select-top5-clients-for-transport"
			);
			if (response.status === 200) {
				const result = response.data;
				const mapedResults = result.map(
					(item: ITop5ClientsForPaymentsDto) => ({
						label: item.nameAndSurname,
						value: item.paymentAmount,
						percentage: item.paymentPercentage,
					})
				);
				setTop5lientsForTransportPayments(mapedResults);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getTop5ClientsForOrderPayments = async () => {
		try {
			const response = await api.get(
				"Dashboard/select-top5-clients-for-orders"
			);
			if (response.status === 200) {
				const result = response.data;
				const mapedResults = result.map(
					(item: ITop5ClientsForPaymentsDto) => ({
						label: item.nameAndSurname,
						value: item.paymentAmount,
						percentage: item.paymentPercentage,
					})
				);
				setTop5lientsForOrderPayments(mapedResults);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getInternationalWeeklyStatistics = async () => {
		try {
			const response = await api.get(
				"Dashboard/select-international-weekly-statistics"
			);
			if (response.status === 200) {
				const result =
					response.data as IInternationalWeeklyStatisticsDto[];
				const groupedMap: Record<string, VolumeServiceData> = {};
				for (const item of result) {
					const shortDay =
						dayAbbreviations[item.deliveredDayName] ||
						item.deliveredDayName;

					if (!groupedMap[shortDay]) {
						groupedMap[shortDay] = {
							name: shortDay,
							ks: 0,
							al: 0,
							mk: 0,
						};
					}
					switch (item.zoneName) {
						case "KS":
							groupedMap[shortDay].ks =
								item.numberOfDeliveredOrders;
							break;
						case "AL":
							groupedMap[shortDay].al =
								item.numberOfDeliveredOrders;
							break;
						case "MK":
							groupedMap[shortDay].mk =
								item.numberOfDeliveredOrders;
							break;
					}
				}
				const mappedResults = Object.values(groupedMap);

				setInternationalWeeklyStatistics(mappedResults);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getDeliveredOrReturnedOrders = useCallback(
		async (startDate: any, endDate: any, selectedCountry: any) => {
			try {
				const formattedStartDate = startDate
					? startDate?.toISOString().split("T")[0]
					: null;
				const formattedEndDate = endDate
					? endDate?.toISOString().split("T")[0]
					: null;
				const response = await api.get<
					ISuccessRateDeliveredOrReturnedDto[]
				>("Dashboard/select-success-rate-delivered-or-returned", {
					params: {
						fromDate: formattedStartDate,
						toDate: formattedEndDate,
						countryId: selectedCountry,
					},
				});

				if (response.status === 200 && response.data) {
					const results = response.data;

					const groupedMap: Record<string, IWeeklyOrdersData> = {};

					for (const item of results) {
						const shortDay =
							dayAbbreviations[item.dayName] || item.dayName;

						if (!groupedMap[shortDay]) {
							groupedMap[shortDay] = {
								day: shortDay,
								dorezuar: 0,
								kthyer: 0,
							};
						}

						if (item.statusName === "Delivered") {
							groupedMap[shortDay].dorezuar = item.numberOfOrders;
						} else if (item.statusName === "Returned") {
							groupedMap[shortDay].kthyer = item.numberOfOrders;
						}
					}

					const mappedResults = Object.values(groupedMap);

					setWeeklyData(mappedResults);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		},
		[]
	);

	const translateOrderDescriptions = (data: any, strings: any) => {
		return data?.map((item: any) => {
			let translatedDescription = item.description;

			switch (item.id) {
				case 1:
					translatedDescription = strings?.registered;
					break;
				case 5:
					translatedDescription = strings?.transport;
					break;
				case 2:
					translatedDescription = strings?.waitingPickup;
					break;
				case 4:
					translatedDescription = strings?.inStorage;
					break;
				case 6:
					translatedDescription = strings?.delivered;
					break;
				case 7:
					translatedDescription = strings?.rejected;
					break;
				case 8:
					translatedDescription = strings?.pending;
					break;
				default:
					break;
			}

			return {
				...item,
				description: translatedDescription,
			};
		});
	};

	const SelectOrderCountByCountry = async () => {
		try {
			const response = await api.get("Order/order-count-by-country");
			if (response.status === 200) {
				let result = response?.data;
				orderCountByCountry(result);
			} else {
				return [];
			}
		} catch (error) {
			console.log(error);
		}
	};

	const orderCountByCountry = (data: any) => {
		if (data) {
			const updatedData = tableOrderByCountries.map((item) => {
				const countryData = data?.find(
					(data: any) => data.country === item.country
				);
				if (countryData) {
					return {
						...item,
						orderCount: countryData.orderCount,
					};
				}
				return item;
			});
			setTableOrderByCountries(updatedData);
		}
	};

	const calculateOrderPercentages = (ordersByCity: any) => {
		const totalOrders = ordersByCity.reduce(
			(sum: any, city: any) => sum + city.orderCount,
			0
		);

		const updatedOrdersByCity = ordersByCity.map((city: any) => ({
			...city,
			percentage:
				totalOrders > 0
					? ((city.orderCount / totalOrders) * 100).toFixed(2)
					: 0,
		}));

		return updatedOrdersByCity;
	};

	const selectDeliveredOrderCountByCities = async () => {
		try {
			const response = await api.get(
				"Dashboard/select-delivered-orders-by-city"
			);
			if (response.status === 200) {
				const result = response?.data;
				const ordersWithPercentages = calculateOrderPercentages(result);
				ordersWithPercentages.sort(
					(a: any, b: any) => b.orderCount - a.orderCount
				);
				const mapResult = ordersWithPercentages.map(
					(item: OrderCountByCity) => ({
						label: item.city,
						value: item.orderCount,
						percentage: item.percentage,
					})
				);
				setDeliveredOrdersCountByCityForMap(ordersWithPercentages);
				setDeliveredOrdersCountByCity(mapResult);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const selectReturnedOrderCountByCities = async () => {
		try {
			const response = await api.get(
				"Dashboard/select-returned-orders-by-city"
			);
			if (response.status === 200) {
				const result = response?.data;
				const ordersWithPercentages = calculateOrderPercentages(result);
				ordersWithPercentages.sort(
					(a: any, b: any) => b.orderCount - a.orderCount
				);
				const mapResult = ordersWithPercentages.map(
					(item: OrderCountByCity) => ({
						label: item.city,
						value: item.orderCount,
						percentage: item.percentage,
					})
				);
				setReturnedOrdersCountByCityForMap(ordersWithPercentages);
				setReturnedOrdersCountByCity(mapResult);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const selectStatisticsForOrdersInProcess = useCallback(
		async (startDate: any, endDate: any, selectedCountry: any) => {
			try {
				const formattedStartDate = startDate
					? startDate.toISOString().split("T")[0]
					: null;
				const formattedEndDate = endDate
					? endDate.toISOString().split("T")[0]
					: null;
				const response = await api.get(
					"Dashboard/select-orders-in-process",
					{
						params: {
							fromDate: formattedStartDate,
							toDate: formattedEndDate,
							countryId: selectedCountry,
						},
					}
				);
				if (response.status === 200) {
					const result: IOrderProcessStatisticsDto[] = response?.data;
					const processData: IProcessData[] = result.map((dto) => ({
						date: new Date(dto.theDate).toLocaleDateString(
							"en-GB",
							{
								day: "2-digit",
								month: "short",
								year: "numeric",
							}
						),
						inTransport: dto.transportingCount,
						inStorage: dto.inStorageCount,
						pending: dto.pendingCount,
					}));
					setStatisticsForOrdesInProcess(processData);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		},
		[]
	);

	const [pieChartData, setPieChartData] = useState<IPieChartData[]>([]);

	const selectStatisticsForDailySales = async () => {
		try {
			const response = await api.get(
				"Dashboard/select-todays-deliveries-by-zone"
			);
			if (response.status === 200) {
				const result: IZoneDeliveryDto[] = response?.data;
				const mappedData = result.map((item, index) => ({
					id: index,
					value: item.numberOfDeliveredOrders,
					zoneName: item.zoneName,
					zoneDescription: zoneMapping[item.zoneName] || "",
					color: colorMapping[item.zoneName] || "#000000",
				}));
				setPieChartData(mappedData);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const getFinances = async () => {
		try {
			const response = await api.get("Finance/dashboard-finances");
			if (response) {
				if (response.status === 200) {
					const result = response.data[0];
					dispatch(financeStatistic(result));
					setCompanyBalance(result);
				}
			}
		} catch (error) {
			console.log(error);
			setCompanyBalance([]);
		}
	};

	const [orderDataForChart, setOrderDataForChart] = useState<any[]>([]);

	const getSelectedOrdersByMonth = useCallback(
		async (startDate: any, endDate: any, selectedCountry: any) => {
			try {
				const formattedStartDate = startDate
					? startDate.toISOString().split("T")[0]
					: null;
				const formattedEndDate = endDate
					? endDate.toISOString().split("T")[0]
					: null;
				const response = await api.get(
					"Dashboard/order-count-by-month",
					{
						params: {
							fromDate: formattedStartDate,
							toDate: formattedEndDate,
							countryId: selectedCountry,
						},
					}
				);
				if (response) {
					if (response.status === 200) {
						const result = response.data;
						const formattedResult = result.map(
							(item: IOrderDataChart) => {
								const [year, month] =
									item.orderMonth.split("-");
								const monthName =
									albanianMonths[parseInt(month, 10) - 1];
								return {
									...item,
									// monthName: `${monthName} ${year}`,
									monthName: item.orderMonth,
								};
							}
						);
						setOrderDataForChart(formattedResult);
					}
				}
			} catch (error) {
				console.log(error);
				setCompanyBalance([]);
			}
		},
		[]
	);

	const fetchCountryId = async () => {
		try {
			const response = await api.get("Zone/country-id");
			if (response.status === 200) {
				setCountryId(response.data);
			}
		} catch (error) {
			console.error("Error fetching country ID:", error);
		}
	};
	const [isLoading, setLoading] = useState(false);

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		userRoleId === userRoles.CLIENT || userRoleId === userRoles.SUBCLIENT
			? setIsClient(true)
			: setIsClient(false);

		setLoading(true);

		Promise.all([
			SelectOrderCountByCountry(),
			selectDeliveredOrderCountByCities(),
			selectReturnedOrderCountByCities(),
			getOrderCount(null, null, null),
			getFinances(),
			getDailyOrderList(),
			fetchCountryId(),
			getSelectedOrdersByMonth(null, null, null),
			getTop5ClientsForTransportPayments(),
			getTop5ClientsForOrderPayments(),
			getInternationalWeeklyStatistics(),
			getDeliveredOrReturnedOrders(null, null, null),
			selectStatisticsForOrdersInProcess(null, null, null),
			selectStatisticsForDailySales(),
			fetchZones()
				.then((response) => {
					if (Array.isArray(response)) {
						setZones(response);
					} else {
						console.error(
							"Expected an array for zones, but received:",
							response
						);
						setZones([]);
					}
				})
				.catch((error) => {
					console.error("Error fetching zones:", error);
					setZones([]);
				}),
		])
			.then(
				([
					orderCountry,
					deliveredCities,
					returnedCities,
					orderCountData,
					financeData,
				]) => {}
			)
			.catch((error) => {
				console.error("Error fetching dashboard data:", error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, []);

	useEffect(() => {
		if (strings && data.length > 0) {
			orderCountByStatus(data);
		}
	}, [strings, data]);

	let sliderRef = useRef<Slider | null>(null);
	const next = () => {
		sliderRef?.current?.slickNext();
	};
	const previous = () => {
		sliderRef?.current?.slickPrev();
	};
	const settings = {
		dots: false,
		infinite: false,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
	};

	const englishMonths = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];

	const macedonianMonths = [
		"Јануари",
		"Февруари",
		"Март",
		"Април",
		"Мај",
		"Јуни",
		"Јули",
		"Август",
		"Септември",
		"Октомври",
		"Ноември",
		"Декември",
	];

	const monthNames =
		lang === "al"
			? albanianMonths
			: lang === "mk"
			? macedonianMonths
			: englishMonths;

	const today = new Date();
	const fourMonthsAgo = new Date();
	fourMonthsAgo.setMonth(today.getMonth() - 4);

	const formatDate = (date: any) => {
		const day = date.getDate();
		const month = monthNames[date.getMonth()];
		const year = date.getFullYear();
		return `${day} ${month} ${year}`;
	};

	const statsSubtitle = `(${formatDate(fourMonthsAgo)} - ${formatDate(
		today
	)})`;

	const renderCountryOptions = () => {
		const countryOptions = zones
			.filter((country: Zone) => country.levelId === 1)
			.map((country: Zone) => ({
				value: country.id,
				label: country.description,
			}));

		return [...countryOptions];
	};

	const handleResetFilters = async () => {
		setSelectedCountry(null);
		setStartDate(null);
		setEndDate(null);
		await getOrderCount(null, null, null);
		await getDeliveredOrReturnedOrders(null, null, null);
		await selectStatisticsForOrdersInProcess(null, null, null);
		await getSelectedOrdersByMonth(null, null, null);
	};
	const [selectedToggle, setSelectedToggle] = useState<"dorëzuar" | "kthyer">(
		"dorëzuar"
	);
	if (isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					width: "100%",
					marginTop: "10px",
				}}
			>
				<Skeleton style={{ width: "85%" }} />
			</div>
		);
	}

	const onFilterCountrySelected = async (value: any) => {
		if (!value) {
			setSelectedCountry(null);
		} else {
			setSelectedCountry(value);
			setCountryId(value);
		}
	};

	const onFromDateChanged = async (date: any) => {
		if (!date) {
			setStartDate(null);
		} else {
			setStartDate(date);
		}
	};

	const onToDateChanged = async (date: any) => {
		if (!date) {
			setEndDate(null);
		} else {
			setEndDate(date);
		}
	};

	const handleFilterSubmit = async () => {
		await getOrderCount(startDate, endDate, selectedCountry);
		await getDeliveredOrReturnedOrders(startDate, endDate, selectedCountry);
		await selectStatisticsForOrdersInProcess(
			startDate,
			endDate,
			selectedCountry
		);
		await getSelectedOrdersByMonth(startDate, endDate, selectedCountry);
	};

	return (
		<>
			<div className="dashboardContainer">
				<div className="dashboardWrapper">
					<div className="filtersWrapper">
						<div className="text-xl font-semibold">
							{strings?.mainDashboard}
						</div>
						<div
							className="filtersContainer"
							style={{
								display: "flex",
								gap: "15px",
								alignItems: "flex-end",
								flexWrap: "wrap",
							}}
						>
							<DefaultSelect
								label={strings?.country}
								placeholder={strings?.chooseCountry}
								options={renderCountryOptions()}
								value={selectedCountry ?? undefined}
								onChange={onFilterCountrySelected}
								allowClear
								flexDirection="col"
								alignCenter="start"
								className={classes.input}
							/>
							<div className={classes.datat}>
								<DefaultDatePicker
									label={strings?.fromdate}
									value={startDate}
									onChange={onFromDateChanged}
									width={172}
									className={classes.firstData}
								/>
								<DefaultDatePicker
									label={strings?.untill}
									value={endDate}
									onChange={onToDateChanged}
									width={172}
									className={classes.secondData}
								/>
							</div>
							<Button
								onClick={handleFilterSubmit}
								// disabled={filterButtonLoading}
								style={{
									width: "9.25rem",
									height: "42px",
									background: "#FFFFFF",
									borderRadius: "4px",
									color: "white",
									backgroundColor: "#4f16c2",
								}}
							>
								{"Filtro"}
							</Button>
							<Button
								style={{
									width: "9.25rem",
									height: "42px",
									background: "#FFFFFF",
									borderRadius: "4px",
									color: "#4f16c2",
								}}
								onClick={handleResetFilters}
							>
								{strings?.reset}
							</Button>
						</div>
					</div>

					<div className="firstDashboardContainer">
						<OrderStatus />

						<div className="statisticDashboard">
							<div className="statisticFirst">
								<p className="statisticParagraph">
									<b>{strings?.statsTitle}</b>
								</p>
								<div className="statisticSecond">
									<p>
										<span className="text-[#4F16C2]">
											•
										</span>{" "}
										{strings?.delivered}
									</p>
									<p>
										<span className="text-[#FF2828]">
											•
										</span>{" "}
										{strings?.returned}
									</p>
								</div>
							</div>
							<ResponsiveContainer width="100%" height={250}>
								<AreaChart
									width={1100}
									height={250}
									data={orderDataForChart}
									margin={{
										top: 10,
										right: 30,
										left: 0,
										bottom: 0,
									}}
								>
									<defs>
										<linearGradient
											id="colorUv"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="20%"
												stopColor="#8884d8"
												stopOpacity={0.8}
											/>
											<stop
												offset="95%"
												stopColor="#8884d8"
												stopOpacity={0}
											/>
										</linearGradient>
										<linearGradient
											id="colorRv"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="20%"
												stopColor="#ff0000"
												stopOpacity={0.8}
											/>
											<stop
												offset="95%"
												stopColor="#ff0000"
												stopOpacity={0}
											/>
										</linearGradient>
									</defs>
									<XAxis dataKey="monthName" />
									<YAxis />
									<CartesianGrid strokeDasharray="3 3" />
									<Tooltip />
									<Area
										type="monotone"
										dataKey="deliveredOrders"
										stroke="#4F16C2"
										fillOpacity={1}
										fill="url(#colorUv)"
										name={strings?.delivered}
									/>
									<Area
										type="monotone"
										dataKey="rejectedOrders"
										stroke="#ff0000"
										fillOpacity={1}
										fill="url(#colorRv)"
										name={strings?.returned}
									/>
								</AreaChart>
							</ResponsiveContainer>
						</div>
					</div>

					<div className="secondStatisticDashboard">
						<ProcessChart
							title={strings?.processChart}
							transportString={strings?.transport}
							inStorageString={strings?.inStorage}
							pendingString={strings?.pending}
							data={statisticsForOrdesInProcess}
						/>
						<SuccessRateChart
							data={weeklyData}
							title={strings?.successRate}
							deliveredString={strings?.delivered}
							returnedString={strings?.returned}
						/>
					</div>
				</div>

				<div className="progressListAndLatestStatics">
					{isClient ? (
						<>
							<VolumeServiceLevelChart
								data={internationalWeeklyStatistics}
								title={strings?.internationalWeeklyStatistics}
							/>
							<DailySalesPieChart
								data={pieChartData}
								title={"Shitjet Ditore"}
							/>
						</>
					) : (
						<>
							<ProgressListTable
								title={strings?.topClients}
								items={top5ClientsForTransportPayments ?? []}
								progressBarColor={"#4F16C2"}
								subTitle={
									strings?.subtitleForTranasportPayments
								}
							/>
							<ProgressListTable
								title={strings?.topClients}
								items={top5ClientsForOrderPayments ?? []}
								progressBarColor={"#28BFFF"}
								subTitle={strings?.subtitleForOrderPayments}
							/>
						</>
					)}
					<div className="thirdStatisticDiv">
						<Slider
							{...settings}
							ref={(slider) => {
								sliderRef.current = slider;
							}}
						>
							{dailyOrderList.length === 0 ? (
								<>
									<p className="StatisticTitle">
										{strings?.noOrdersToday}
									</p>
								</>
							) : (
								dailyOrderList.map((order: any) => (
									<div
										key={order.id}
										className="orderContainer"
									>
										<div className="thirdDivInfo">
											<h1 className="StatisticTitle">
												{strings?.latestOrders}
											</h1>
											{dailyOrderList.length > 1 && (
												<div className="buttonsSlider">
													<button
														className="button"
														onClick={previous}
													>
														<SliderLeft />
													</button>
													<button
														className="button"
														onClick={next}
													>
														<SliderRight />
													</button>
												</div>
											)}
										</div>
										<div className="latestOrderInfo">
											<h1 className="latestOrderNumber">
												#{order.displayId}
											</h1>
											<h1 className="latestOrderDesc">
												{order.orderDescription}
											</h1>
										</div>
										<div className="orderTransportInfo">
											<h1 className="orderTransportFirst">
												{order.statusChangeComment}
											</h1>
											<h2 className="orderTransportSecond">
												{order.fullName} - {order.phone}
											</h2>
										</div>
										<div className="orderLastInfo">
											<div className="clockDiv">
												<p className="clockInfo">
													<ClockIcon />
													{order.statusTime}
												</p>
												<p className="clockInfo">
													<CalendarIcon />
													{new Date(
														order.statusDate
													).toLocaleDateString()}
												</p>
											</div>
											<div className="buttonInfo">
												<ButtonIcon />
												<Button
													className="buttonInfoLast"
													onClick={() =>
														navigate(
															`/order-details?id=${order.orderId}`
														)
													}
												>
													{strings?.orderDetails}
												</Button>
											</div>
										</div>
									</div>
								))
							)}
						</Slider>
					</div>
				</div>
				<div className="thirdStatisticDashboard">
					<div className="mapStaticsInfo">
						<div className="hartInfo">
							<div className="flex flex-row justify-between p-4">
								<div className="flex flex-col gap-4">
									<h1 className="StatisticTitle">
										{strings?.map}
									</h1>
									<DefaultSelect
										label={strings?.country}
										placeholder={strings?.chooseCountry}
										options={renderCountryOptions()}
										value={countryId ?? undefined}
										onChange={(value: any) =>
											setCountryId(value)
										}
										allowClear
										flexDirection="col"
										alignCenter="start"
										className={classes.input}
									/>
								</div>
								<MySliderToggle
									value={selectedToggle}
									onChange={(newValue) =>
										setSelectedToggle(newValue)
									}
									deliveredString={strings?.delivered}
									returnedString={strings?.returned}
								/>
							</div>
							<h2 className="hartDescTitle">
								<Map
									ordersCountByCity={
										selectedToggle === "dorëzuar"
											? deliveredOrdersCountByCityForMap
											: returnedOrdersCountByCityForMap
									}
									countryId={countryId}
									selectedToggle={selectedToggle}
								/>
							</h2>
						</div>
						<ProgressListTable
							title={strings?.ordersByCity}
							items={
								selectedToggle === "dorëzuar"
									? deliveredOrdersCountByCity
									: returnedOrdersCountByCity
							}
							style={{
								backgroundColor: "#FBFBFB",
								height: "85%",
							}}
							progressBarColor={"#CFBFEE"}
						/>
					</div>
					{!isClient && (
						<VolumeServiceLevelChart
							style={{ height: "85%" }}
							data={internationalWeeklyStatistics}
							title={strings?.internationalWeeklyStatistics}
						/>
					)}
				</div>
			</div>
		</>
	);
};

export default Dashboard;
